import camelcase from 'lodash.camelcase'

import { ACORNS_HELLO_FLOW, VISITED_PAGES } from 'app-constants'
import { LinkedAccountExternalSourceEnum, LinkedAccountSourceFragment } from 'generated/types'
import { sendToSentry } from 'utils/errors'
import { NavSteps as LaterOnboardingNavSteps } from 'utils/FlowProvider/flows/laterOnboarding/nav'
import removeDuplicateQueryParams from 'utils/sourceAttributes'

import { IS_REGISTRATION_FLOW } from './index'
import { Flow, FlowRoute } from './types'

export const VALID_FLOWS = [
  'checkingOnboarding',
  'earlyOnboarding',
  'mocOnboarding',
  'laterOnboarding',
  'investOnboarding',
  'plaidLayer',
] as const
const VALID_FLOWS_REGEX = new RegExp(`^(${VALID_FLOWS.join('|')})$`)

export const navStepMap = new Map([['laterOnboarding', LaterOnboardingNavSteps]])

export const DEFAULT_FUNNEL_NAME: Flow = 'plaidLayer'

/**
 * Determines if user is in a 'default' funnel. This is any funnel that we allow the user
 * to start onboarding with at the beginning of registration. Any product onboarding
 * funnels would not be considered default flows.
 *
 * @return Flag indicating if current funnel is considered default or not.
 */

export const getValidFlow = (flow): Flow =>
  String(flow).match(VALID_FLOWS_REGEX) ? flow : DEFAULT_FUNNEL_NAME

// Gets the flow for the current user. If not in the query string, checks the sessionStorage or uses 'plaidLayer' by default
export const getFlow = (): Flow => {
  let flow
  try {
    const parsed = removeDuplicateQueryParams(window.location.search)
    if (parsed.flow) {
      flow = getValidFlow(camelcase(parsed.flow))
    } else if (window.localStorage.getItem(ACORNS_HELLO_FLOW)) {
      flow = getValidFlow(window.localStorage.getItem(ACORNS_HELLO_FLOW))
    } else {
      flow = DEFAULT_FUNNEL_NAME
    }
    window.localStorage.setItem(ACORNS_HELLO_FLOW, flow)
  } catch (e) {
    sendToSentry(e, {
      name: 'FlowProviderError',
      message: 'Cannot resolve flow. Using default flow instead.',
    })
    flow = DEFAULT_FUNNEL_NAME
  }

  return flow
}

export const getFunnelName = (flowName: Flow) => {
  const isRegistration = isRegistrationFlow()

  switch (flowName) {
    case 'investOnboarding':
      return isRegistration ? 'PlaidLayer' : 'dynamic-reg-invest'
    case 'laterOnboarding': {
      return isRegistration ? 'dynamic-reg-later' : 'laterOnboarding'
    }
    case 'checkingOnboarding':
      return 'dynamic-reg-checking'
    case 'mocOnboarding':
      return 'dynamic-reg-checking'
    case 'earlyOnboarding':
      return 'dynamic-reg-early'
    default:
      return 'PlaidLayer'
  }
}

export const isRegistrationFlow = (): boolean => {
  return window.sessionStorage.getItem(IS_REGISTRATION_FLOW) === 'true'
}

const investingPages = [FlowRoute.RecurringInvestment, FlowRoute.OneTimeInvestment]

export const investingPageVisited = (visitedPages) => {
  const visited = investingPages.some((page) => visitedPages.includes(page))
  return visited
}

export const getVisitedPages = () => {
  return JSON.parse(window.localStorage.getItem(VISITED_PAGES) || '[]')
}

export const checkIsEligibleRoundUps = (linkedAccounts: LinkedAccountSourceFragment[]) => {
  let isEligibleRoundUps = false

  if (linkedAccounts) {
    for (const linkedAccount of linkedAccounts) {
      if (
        linkedAccount?.externalSource === LinkedAccountExternalSourceEnum.spend ||
        linkedAccount?.externalSource === LinkedAccountExternalSourceEnum.direct_bank ||
        linkedAccount?.externalSource === LinkedAccountExternalSourceEnum.plaid
      ) {
        isEligibleRoundUps = true
      }
    }
  }
  return isEligibleRoundUps
}
